import React, { useState, useEffect, useRef } from 'react';
import {
    Box, Typography, Paper, Grid, TextField, Button, MenuItem,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Autocomplete, Dialog, DialogTitle, DialogContent, DialogActions,
    Chip, Tabs, Tab
} from '@mui/material';
import { Print as PrintIcon, Add as AddIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { studentAPI, depositAPI, academicYearAPI } from '../services/api';
import { toast } from 'react-toastify';
const FIXED_AMOUNT = 5000;

const RefundableDeposit = () => {
    const printRef = useRef();
    const [tab, setTab] = useState(0);
    const [deposits, setDeposits] = useState([]);
    const [stats, setStats] = useState({});
    const [students, setStudents] = useState([]);
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [academicYears, setAcademicYears] = useState([]);
    const [selectedYear, setSelectedYear] = useState('');
    const [paymentMode, setPaymentMode] = useState('cash');
    const [loading, setLoading] = useState(false);
    const [selectedDeposit, setSelectedDeposit] = useState(null);
    const [refundDialog, setRefundDialog] = useState(false);
    const [refundRemarks, setRefundRemarks] = useState('');

    useEffect(() => {
        fetchAcademicYears();
        fetchDeposits();
        fetchStats();
    }, []);

    const fetchAcademicYears = async () => {
        try {
            const res = await academicYearAPI.getAll();
            if (res.data.success) {
                setAcademicYears(res.data.data);
                const current = res.data.data.find(y => y.isCurrent);
                if (current) setSelectedYear(current.id);
            }
        } catch (error) {
            console.error('Error fetching academic years:', error);
        }
    };

    const fetchDeposits = async () => {
        setLoading(true);
        try {
            const res = await depositAPI.getAll();
            if (res.data.success) setDeposits(res.data.data);
        } catch (error) {
            console.error('Error fetching deposits:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchStats = async () => {
        try {
            const res = await depositAPI.getStats();
            if (res.data.success) setStats(res.data.data);
        } catch (error) {
            console.error('Error fetching stats:', error);
        }
    };

    const handleStudentSearch = async (searchValue) => {
        if (!searchValue || searchValue.length < 2) return;
        try {
            const res = await studentAPI.getAll({ search: searchValue });
            setStudents(res.data.data || []);
        } catch (error) {
            console.error('Error searching students:', error);
        }
    };

    const handleCreateDeposit = async () => {
        if (!selectedStudent) {
            toast.error('Please select a student');
            return;
        }

        try {
            const res = await depositAPI.create({
                studentId: selectedStudent.id,
                academicYearId: selectedYear,
                paymentMode
            });
            if (res.data.success) {
                toast.success('Deposit created successfully!');
                setSelectedDeposit(res.data.data);
                setSelectedStudent(null);
                fetchDeposits();
                fetchStats();
            } else {
                toast.error(res.data.error);
            }
        } catch (error) {
            toast.error('Error creating deposit');
        }
    };

    const handleRefund = async () => {
        if (!selectedDeposit) return;

        try {
            const res = await depositAPI.refund(selectedDeposit.id, { remarks: refundRemarks });
            if (res.data.success) {
                toast.success('Deposit refunded successfully!');
                setRefundDialog(false);
                setSelectedDeposit(null);
                setRefundRemarks('');
                fetchDeposits();
                fetchStats();
            } else {
                toast.error(res.data.error);
            }
        } catch (error) {
            toast.error('Error refunding deposit');
        }
    };

    const handlePrint = () => {
        if (!selectedDeposit) return;
        const printContent = printRef.current;
        const printWindow = window.open('', '_blank');
        printWindow.document.write(`
            <html>
            <head>
                <title>Refundable Deposit - ${selectedDeposit.receiptNo}</title>
                <style>
                    @page { size: A4 landscape; margin: 5mm; }
                    * { margin: 0; padding: 0; box-sizing: border-box; }
                    body { font-family: Arial, sans-serif; font-size: 11px; }
                    .deposit-container { display: flex; width: 100%; }
                    .deposit-copy { flex: 1; border: 2px solid #000; padding: 8px; margin: 3px; }
                    .copy-header { text-align: center; font-weight: bold; border-bottom: 2px solid #000; padding-bottom: 5px; margin-bottom: 8px; font-size: 12px; }
                    .school-name { font-size: 14px; font-weight: bold; text-align: center; margin-bottom: 5px; }
                    .subtitle { text-align: center; font-weight: bold; margin-bottom: 10px; }
                    .info-row { display: flex; justify-content: space-between; margin: 4px 0; }
                    .amount-box { border: 2px solid #000; padding: 10px; text-align: center; margin: 15px 0; }
                    .amount-value { font-size: 24px; font-weight: bold; }
                    .amount-words { font-style: italic; margin-top: 5px; }
                    .signature-row { display: flex; justify-content: space-between; margin-top: 20px; }
                </style>
            </head>
            <body>
                ${printContent.innerHTML}
            </body>
            </html>
        `);
        printWindow.document.close();
        printWindow.print();
    };

    const formatDate = (date) => {
        if (!date) return '';
        const d = new Date(date);
        return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
    };

    const DepositCopy = ({ title, deposit }) => (
        <div className="deposit-copy">
            <div className="copy-header">{title}</div>
            <div className="school-name">VP Super English Med. School, Supe</div>
            <div className="subtitle">REFUNDABLE DEPOSIT</div>

            <div className="info-row">
                <span>Receipt No: {deposit.receiptNo}</span>
                <span>Date: {formatDate(deposit.depositDate)}</span>
            </div>
            <div className="info-row">
                <span>Name: {deposit.student?.user?.firstName} {deposit.student?.user?.lastName}</span>
            </div>
            <div className="info-row">
                <span>Class: {deposit.student?.class?.name}</span>
                <span>Year: {deposit.academicYear?.name}</span>
            </div>
            <div className="info-row">
                <span>Payment Mode: {deposit.paymentMode?.toUpperCase()}</span>
            </div>

            <div className="amount-box">
                <div className="amount-value">₹ {deposit.amount}/-</div>
                <div className="amount-words">{deposit.amountInWords}</div>
            </div>

            <div className="signature-row">
                <div>_____________<br />Depositor Sign</div>
                <div>_____________<br />Receiver Sign</div>
            </div>
        </div>
    );

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h5" gutterBottom>Refundable Deposit (₹{FIXED_AMOUNT}/-)</Typography>

            {/* Stats Cards */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={3}>
                    <Paper sx={{ p: 2, bgcolor: 'success.light', color: 'white' }}>
                        <Typography>Active Deposits</Typography>
                        <Typography variant="h4">₹{stats.totalActive || 0}</Typography>
                        <Typography variant="caption">({stats.activeCount || 0} students)</Typography>
                    </Paper>
                </Grid>
                <Grid item xs={12} md={3}>
                    <Paper sx={{ p: 2, bgcolor: 'info.light', color: 'white' }}>
                        <Typography>Refunded</Typography>
                        <Typography variant="h4">₹{stats.totalRefunded || 0}</Typography>
                        <Typography variant="caption">({stats.refundedCount || 0} refunds)</Typography>
                    </Paper>
                </Grid>
            </Grid>

            <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 2 }}>
                <Tab label="Create Deposit" />
                <Tab label="All Deposits" />
            </Tabs>

            {tab === 0 && (
                <Paper sx={{ p: 3 }}>
                    {!selectedDeposit ? (
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <Autocomplete
                                    options={students}
                                    getOptionLabel={(option) =>
                                        `${option.user?.firstName} ${option.user?.lastName} - ${option.admissionNumber}`
                                    }
                                    onInputChange={(e, value) => handleStudentSearch(value)}
                                    onChange={(e, value) => setSelectedStudent(value)}
                                    renderInput={(params) => (
                                        <TextField {...params} label="Search Student" />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    select fullWidth label="Academic Year"
                                    value={selectedYear}
                                    onChange={(e) => setSelectedYear(e.target.value)}
                                >
                                    {academicYears.map(y => (
                                        <MenuItem key={y.id} value={y.id}>{y.name}</MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    select fullWidth label="Payment Mode"
                                    value={paymentMode}
                                    onChange={(e) => setPaymentMode(e.target.value)}
                                >
                                    <MenuItem value="cash">Cash</MenuItem>
                                    <MenuItem value="online">Online</MenuItem>
                                </TextField>
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant="h4" color="primary" gutterBottom>
                                    Fixed Amount: ₹{FIXED_AMOUNT}/-
                                </Typography>
                                <Button
                                    variant="contained" size="large"
                                    startIcon={<AddIcon />}
                                    onClick={handleCreateDeposit}
                                >
                                    Create Deposit Receipt
                                </Button>
                            </Grid>
                        </Grid>
                    ) : (
                        <Box>
                            <Box sx={{ mb: 2 }}>
                                <Button
                                    variant="contained" startIcon={<PrintIcon />}
                                    onClick={handlePrint} sx={{ mr: 2 }}
                                >
                                    Print Receipt
                                </Button>
                                <Button variant="outlined" onClick={() => setSelectedDeposit(null)}>
                                    New Deposit
                                </Button>
                            </Box>

                            <Paper sx={{ p: 2, overflowX: 'auto' }}>
                                <div ref={printRef}>
                                    <div className="deposit-container" style={{ display: 'flex' }}>
                                        <DepositCopy title="PDCC Copy" deposit={selectedDeposit} />
                                        <DepositCopy title="Student Copy" deposit={selectedDeposit} />
                                        <DepositCopy title="Account Dept Copy" deposit={selectedDeposit} />
                                        <DepositCopy title="School Office Copy" deposit={selectedDeposit} />
                                    </div>
                                </div>
                            </Paper>
                        </Box>
                    )}
                </Paper>
            )}

            {tab === 1 && (
                <Paper>
                    <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button startIcon={<RefreshIcon />} onClick={fetchDeposits}>Refresh</Button>
                    </Box>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Receipt No</TableCell>
                                    <TableCell>Student</TableCell>
                                    <TableCell>Date</TableCell>
                                    <TableCell>Amount</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {deposits.map(dep => (
                                    <TableRow key={dep.id}>
                                        <TableCell>{dep.receiptNo}</TableCell>
                                        <TableCell>
                                            {dep.student?.user?.firstName} {dep.student?.user?.lastName}
                                        </TableCell>
                                        <TableCell>{formatDate(dep.depositDate)}</TableCell>
                                        <TableCell>₹{dep.amount}</TableCell>
                                        <TableCell>
                                            <Chip
                                                label={dep.status}
                                                color={dep.status === 'active' ? 'success' : 'default'}
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Button
                                                size="small"
                                                onClick={() => { setSelectedDeposit(dep); setTab(0); }}
                                            >
                                                View/Print
                                            </Button>
                                            {dep.status === 'active' && (
                                                <Button
                                                    size="small" color="warning"
                                                    onClick={() => { setSelectedDeposit(dep); setRefundDialog(true); }}
                                                >
                                                    Refund
                                                </Button>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            )}

            {/* Refund Dialog */}
            <Dialog open={refundDialog} onClose={() => setRefundDialog(false)}>
                <DialogTitle>Refund Deposit</DialogTitle>
                <DialogContent>
                    <Typography gutterBottom>
                        Refunding ₹{selectedDeposit?.amount} for {selectedDeposit?.student?.user?.firstName}
                    </Typography>
                    <TextField
                        fullWidth multiline rows={3}
                        label="Refund Remarks" margin="dense"
                        value={refundRemarks}
                        onChange={(e) => setRefundRemarks(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRefundDialog(false)}>Cancel</Button>
                    <Button variant="contained" color="warning" onClick={handleRefund}>
                        Confirm Refund
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default RefundableDeposit;
